import React from "react";
import { connect } from "react-redux";
import PDFUtil from "../../util/PDFUtil";
import DateUtil from "../../util/DateUtil";
import { FormattedMessage, injectIntl } from "react-intl";
import OrderProducts from "./OrderProducts";
import OrderStatus from "../../enums/OrderStatus";
import { deleteOrder, duplicate } from "../../actions/orders/orders";
import Notification from "../sub/Notification";
import Maths from "../../util/Maths";
import ActionMenu from "../sub/ActionMenu";
import OrderTemplatePDF from "./OrderTemplatePDF";
import { Alert } from "react-bootstrap";
import PopoverHelper from "../sub/bootstrap/PopoverHelper";
import PriceUtil from "../../util/PriceUtil";
import Icon from "../sub/Icon";

class OrderRow extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      collapsed: true,
      template: "",
      isLoading: false,
      disabledDuplicat: false,
    };
  }

  collapse() {
    this.setState({
      collapsed: !this.state.collapsed,
    });
  }

  deleteOrder(onDelete) {
    onDelete();
  }

  hasUrgent(products) {
    for (let product of products) {
      if (product.urgent) return true;
    }

    return false;
  }

  canPDF(order) {
    return (
      order.status === OrderStatus.PENDING ||
      order.status === OrderStatus.SENT ||
      order.status === OrderStatus.NEW ||
      order.status === OrderStatus.BLOCKED
    ); //|| order.status === Role.RECEIVED
  }

  downloadPDF() {
    if (!this.canPDF(this.props.order)) return;

    this.setState({ isLoading: true });

    const id = "order-pdf" + this.props.order._id;
    const fileName = this.props.order._id;
    const template = (
      <OrderTemplatePDF
        id={id}
        generalSettings={this.props.generalSettings}
        order={this.props.order}
        client={this.props.client}
      />
    );

    const generatePDF = (field, value, id, fileName, css, callback) => {
      this.setState({ [field]: value }, () => {
        PDFUtil.toPDFWithPuppeteer(
          id,
          fileName,
          css,
          callback,
          { url: "toProductPDF" },
          true,
        );
      });
    };

    generatePDF("template", template, id, fileName, "order", () => {
      this.setState({ isLoading: false });
    });
  }

  duplicate(orderId) {
    if (this.state.disabledDuplicat) return;

    this.setState({ disabledDuplicat: true });

    const successCallback = () => {
      Notification({
        type: "info",
        description: this.props.intl.formatMessage({ id: "Cart.Duplicate" }),
      });

      this.setState({ disabledDuplicat: false });
    };

    this.props.onDuplicate(orderId, successCallback);
  }

  isMercurialActive(mercurialId) {
    return mercurialId === this.props.user.mercurial_Id;
  }

  render() {
    const orderHasProducts = this.props.order.products.length > 0;

    // 'Delete order' modal setup
    const modalTitle = <FormattedMessage id="Order.Remove" />;
    const modalContent = (
      <div>
        <Alert variant="warning">
          <h5>
            <Icon icon="triangle-exclamation" className="me-2" />
            <FormattedMessage id="Warning" />
          </h5>
          <p>
            <FormattedMessage id="Order.Remove.Warning.1.Client" />
          </p>
        </Alert>

        <p>
          <FormattedMessage id="Order.Remove.Confirmation" />
        </p>
      </div>
    );
    const successCallback = () =>
      this.deleteOrder(() =>
        this.props.onDeleteOrder({ orderId: this.props.order._id }),
      );

    let orderStatusClassName = "light";
    if (orderHasProducts) {
      switch (parseInt(this.props.order.status)) {
        case OrderStatus.NEW:
          orderStatusClassName = "danger";
          break;
        case OrderStatus.PENDING:
          orderStatusClassName = "info";
          break;
        case OrderStatus.SENT:
          orderStatusClassName = "success";
          break;
        case OrderStatus.BLOCKED:
          orderStatusClassName = "warning";
          break;
        case OrderStatus.CANCELED:
          orderStatusClassName = "secondary";
          break;
        default:
          break;
      }
    }

    const isDuplicable =
      this.isMercurialActive(this.props.order.products[0].mercurial_id) &&
      this.props.order.status !== OrderStatus.PENDING &&
      !this.state.disabledDuplicat;

    let menuItems = [];

    menuItems.push(
      // Show/hide products
      orderHasProducts && {
        id: "collapse" + this.props.order._id,
        icon: this.state.collapsed ? "eye" : "eye-slash",
        disabled: !orderHasProducts,
        action: () => orderHasProducts && this.collapse(),
        text: this.state.collapsed ? (
          <FormattedMessage id="Display.Product.Order" />
        ) : (
          <FormattedMessage id="Hide.Product.Order" />
        ),
      },
      // Download PDF
      orderHasProducts && {
        id: "downloadPdf" + this.props.order._id,
        icon: "file-pdf",
        disabled: !orderHasProducts,
        action: () => orderHasProducts && this.downloadPDF(),
        text: <FormattedMessage id="Download.PDF" />,
      },
      // Duplicate order
      orderHasProducts &&
        isDuplicable && {
          id: "duplicate" + this.props.order._id,
          icon: "clone",
          disabled: !orderHasProducts,
          action: () =>
            orderHasProducts &&
            isDuplicable &&
            this.duplicate({ orderId: this.props.order._id }),
          text: <FormattedMessage id="Duplicate.Order.To.New.Order" />,
        },
      // Delete order
      this.props.order.status === OrderStatus.NEW && {
        id: "delete" + this.props.order._id,
        icon: "trash",
        action: () =>
          this.props.openConfModal(
            modalTitle,
            modalContent,
            successCallback,
            "danger",
            true,
          ),
        text: <FormattedMessage id="Delete" />,
      },
    );

    const creationDate = DateUtil.toDateWithHour(
      this.props.order.creation_date,
    );
    const deliveryDate = DateUtil.toDate(
      new Date(this.props.order.creation_date).getTime() +
        this.props.order.shipping_delay * 24 * 3600 * 1000,
    );

    return (
      <>
        <tr
          key={this.props.order._id}
          className={
            !orderHasProducts
              ? "text-italic text-muted align-items-center justify-content-center"
              : "align-items-center justify-content-center"
          }
        >
          <td className="align-middle">
            {this.props.order.ref}
            {this.props.order.comment && this.props.comment !== "" && (
              <PopoverHelper
                title={<FormattedMessage id="Available.Comment" />}
              >
                {this.props.order.comment}
              </PopoverHelper>
            )}
          </td>
          <td className="align-middle">{creationDate}</td>
          <td className="align-middle">{deliveryDate}</td>
          <td className="align-middle">
            {PriceUtil.formatEuro(Maths.round(this.props.order.total_ttc))}
          </td>
          <td className={!this.props.limit ? "col-2" : "col-3"}>
            <Alert variant={orderStatusClassName} className="p-1 px-2 mb-0">
              <FormattedMessage
                id={"Order.Status." + this.props.order.status}
              />
            </Alert>
          </td>
          {!this.props.limit && (
            <td className="col-1 text-center">
              {
                <ActionMenu
                  processing={this.state.isLoading}
                  items={menuItems}
                  variant={
                    orderHasProducts &&
                    this.hasUrgent(this.props.order.products) &&
                    "danger"
                  }
                  icon={
                    orderHasProducts &&
                    this.hasUrgent(this.props.order.products) &&
                    "bell"
                  }
                />
              }
            </td>
          )}
        </tr>

        {!this.state.collapsed && (
          <tr>
            <td colSpan="7" className="p-1 py-3 bg-light">
              <OrderProducts order={this.props.order} paginate={true} />
            </td>
          </tr>
        )}

        {this.state.template && (
          <div
            className="d-none"
            style={{ position: "absolute", marginLeft: "-10000px" }}
          >
            {this.state.template}
          </div>
        )}
      </>
    );
  }
}

const mapStateToProps = (state) => {
  return {
    user: state.user,
    generalSettings: state.generalSettings,
    company: state.company,
    mercurials: state.mercurials,
  };
};

const mapDispatchToProps = (dispatch) => {
  return {
    onDeleteOrder: (data) => dispatch(deleteOrder(data)),
    onDuplicate: (data, successCallback) =>
      dispatch(duplicate(data, successCallback)),
  };
};

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(injectIntl(OrderRow));
